import { useRouter } from 'next/router';
import Cookies from 'js-cookie';

import { Heading, Icon, Paragraph, TextButton, UpsellBanner } from '@zillow/constellation-10';
import { IconCameraFilled } from '@zillow/constellation-icons';

import { BuildingPhotographyStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerMediaCreditsWebApiHandler_getMediaCredits } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { MEDIA_CREDITS_COOKIES } from '../../constants/CookieConstants';

type Props = {
  cookieKey: string;
  tone?: 'hero' | 'soft';
};

const MediaCreditsBanner = ({ cookieKey, tone = 'soft' }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string | undefined;

  const { data } = useRentalManagerMediaCreditsWebApiHandler_getMediaCredits({
    meta: {
      tags: ['mediaCredits'],
      context: { api: 'getMediaCredits', details: { listingAlias } },
    },
  });

  if (!listingAlias) {
    return null;
  }

  const dismissKey = `${cookieKey}_${listingAlias}`;

  if (Cookies.get(dismissKey) === 'true') {
    return null;
  }

  const property = data?.properties?.find((p) => p.listingAlias === listingAlias);

  if (
    !property ||
    !property.creditAmount ||
    property.creditAmount <= 0 ||
    !property.packageName ||
    !property.zrnNumber ||
    !property.promoCode ||
    property.buildingPhotographyStatus === BuildingPhotographyStatus.REDEEMED_VOUCHER
  ) {
    return null;
  }

  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(property.creditAmount);

  const handleDismiss = () => {
    Cookies.set(dismissKey, 'true', { expires: MEDIA_CREDITS_COOKIES.EXPIRATION });
  };

  return (
    <UpsellBanner
      tone={tone}
      media={<Icon render={<IconCameraFilled />} />}
      header={
        <Heading level={2} textStyle="heading-xs">
          You have {formattedAmount} in unused media credits
        </Heading>
      }
      body={
        property.aryeoCoverage === false ? (
          <Paragraph>
            This property's outside Zillow Media Experts' coverage area, but our team can still help you redeem your
            credits. Contact 3dtoursupport@zillowgroup.com for support.
          </Paragraph>
        ) : property.promoCode ? (
          <Paragraph>Use code {property.promoCode} to add a 3D tour and help this listing stand out.</Paragraph>
        ) : null
      }
      action={
        property.aryeoCoverage === false ? undefined : (
          <TextButton asChild>
            <a
              href={
                property.orderFormUrl ??
                'https://media.zillowmediaexperts.com/order-forms/01909ec8-d42c-713a-9727-8eb553387fd1'
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Redeem media credits
            </a>
          </TextButton>
        )
      }
      onOpenChange={handleDismiss}
    />
  );
};

export default MediaCreditsBanner;
